import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, AlertCircle, Plus, Pencil, Trash2, Truck, Container } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { api } from '../../services/api';
import AddVehicleForm from '../dashboard/AddVehicleForm';
import HeaderFilter from './HeaderFilter';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const TYPE_LABELS = {
  tractor: 'Tractor', semi_trailer: 'Trailer', truck: 'Truck', other: 'Other',
};

export default function AdminVehicleFleet() {
  const { t } = useLanguage();
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [selUser, setSelUser]   = useState('');   // owner filter (top dropdown)
  const [typeFilter, setTypeFilter] = useState(''); // header icon-filter
  const [search, setSearch]     = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [modal, setModal]       = useState(null);   // { mode:'create'|'edit', vehicle? }
  const [ownerId, setOwnerId]   = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/users/`, { headers: authHeaders() });
      if (res.ok) setUsers(await res.json());
    } catch {}
  }, []);

  const fetchVehicles = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await api.getVehicles();
      setVehicles(Array.isArray(data) ? data : (data.results || []));
    } catch { setError('Failed to load vehicles.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); fetchVehicles(); }, [fetchUsers, fetchVehicles]);

  const usernameById = (id) => users.find(u => String(u.id) === String(id))?.username || '—';

  // What a vehicle is hitched to (tractor→trailer) or hitched by (trailer←tractor).
  const hitchedInfo = (v) => {
    if (v.hitch_to) return v.hitch_to;
    if (v.type === 'semi_trailer') {
      const h = vehicles.find(x => x.hitch_to === v.plate_number);
      if (h) return h.plate_number;
    }
    return '—';
  };

  const openCreate = () => { setOwnerId(''); setModal({ mode: 'create' }); };
  const openEdit   = (v) => { setOwnerId(v.created_by ? String(v.created_by) : ''); setModal({ mode: 'edit', vehicle: v }); };

  const handleSubmit = async (submitData) => {
    // Let errors propagate to AddVehicleForm, which shows them inline (no popup).
    if (modal.mode === 'edit') {
      await api.updateVehicle(modal.vehicle.id, submitData);
    } else {
      await api.addVehicle(submitData);
    }
    setModal(null);
    fetchVehicles();
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteVehicle(id);
      setDeleteId(null);
      fetchVehicles();
    } catch { alert('Failed to delete.'); }
  };

  // ── Filtering ──
  const q = search.trim().toLowerCase();
  const filtered = vehicles.filter(v =>
    (selUser === '' || String(v.created_by) === String(selUser)) &&
    (typeFilter === '' || v.type === typeFilter) &&
    (q === '' || (v.plate_number || '').toLowerCase().includes(q) || (v.vin || '').toLowerCase().includes(q))
  );

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Vehicles of the selected owner — used for hitch options in the modal.
  const ownerVehicles = ownerId ? vehicles.filter(v => String(v.created_by) === String(ownerId)) : [];
  const ownerCountry = users.find(u => String(u.id) === String(ownerId))?.company_country || '';

  const counts = {
    tractor: vehicles.filter(v => v.type === 'tractor').length,
    trailer: vehicles.filter(v => v.type === 'semi_trailer').length,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">{t('vehicleFleet') || 'Vehicle Fleet'}</h1>
        <div className="flex gap-2">
          <button onClick={openCreate} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus size={15}/> Add
          </button>
          <button onClick={fetchVehicles} disabled={loading} className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 flex items-center text-sm">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> {t('refresh')}
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="flex flex-wrap gap-3">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 flex items-center gap-3">
          <Truck className="h-6 w-6 text-primary-600"/>
          <div><div className="text-xs text-gray-500 uppercase">{t('tractor')}</div><div className="text-lg font-bold text-gray-800">{counts.tractor}</div></div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 flex items-center gap-3">
          <Container className="h-6 w-6 text-secondary-600"/>
          <div><div className="text-xs text-gray-500 uppercase">{t('trailer')}</div><div className="text-lg font-bold text-gray-800">{counts.trailer}</div></div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 flex items-center gap-3">
          <div><div className="text-xs text-gray-500 uppercase">Total</div><div className="text-lg font-bold text-gray-800">{vehicles.length}</div></div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select value={selUser} onChange={e => { setSelUser(e.target.value); setCurrentPage(1); }}
          className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">All owners</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
        </select>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input type="text" placeholder="Search plate / VIN…" value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
            className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"/>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm">
          <AlertCircle size={16}/>{error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[
                  { label: 'Plate' },
                  { label: 'Type', filter: 'type' },
                  { label: 'Make / Model' },
                  { label: 'VIN' },
                  { label: t('driverInfo') },
                  { label: t('hitched') || 'Hitched' },
                  { label: 'Owner' },
                  { label: t('actions') },
                ].map(h => (
                  <th key={h.label} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {h.label}
                    {h.filter === 'type' && (
                      <HeaderFilter value={typeFilter}
                        onChange={(v) => { setTypeFilter(v); setCurrentPage(1); }}
                        options={[{ value: '', label: 'All' }, { value: 'tractor', label: 'Tractor' }, { value: 'semi_trailer', label: 'Trailer' }, { value: 'truck', label: 'Truck' }, { value: 'other', label: 'Other' }]}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? [...Array(6)].map((_, i) => (
                <tr key={i}>{[...Array(8)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td>)}</tr>
              )) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-gray-400">No vehicles found</td></tr>
              ) : paged.map(v => (
                <tr key={v.id} className="hover:bg-primary-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{v.plate_number}</td>
                  <td className="px-4 py-3 text-gray-600">{TYPE_LABELS[v.type] || v.type}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{[v.make, v.model].filter(Boolean).join(' ') || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{v.vin || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{v.driver_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{hitchedInfo(v)}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{usernameById(v.created_by)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(v)} className="p-1.5 rounded hover:bg-primary-50 text-primary-600" title="Edit"><Pencil size={15}/></button>
                      <button onClick={() => setDeleteId(v.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Delete"><Trash2 size={15}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && filtered.length > pageSize && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{t('show')}:</span>
              {[20, 50, 100].map(n => (
                <button key={n} onClick={() => { setPageSize(n); setCurrentPage(1); }}
                  className={`px-2 py-1 text-xs rounded ${pageSize === n ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{n}</button>
              ))}
              <span className="text-xs text-gray-500 ml-2">
                {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} / {filtered.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…'); acc.push(p); return acc; }, [])
                .map((p, idx) => p === '…'
                  ? <span key={`e${idx}`} className="px-1 text-xs text-gray-400">…</span>
                  : <button key={p} onClick={() => setCurrentPage(p)}
                      className={`px-2 py-1 text-xs rounded border ${currentPage === p ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 hover:bg-gray-50'}`}>{p}</button>
                )}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">›</button>
            </div>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">{filtered.length} vehicle{filtered.length !== 1 ? 's' : ''}</div>
        )}
      </div>

      {/* Add / Edit modal (adapted user form in admin mode) */}
      {modal && (
        <AddVehicleForm
          isOpen={true}
          onClose={() => setModal(null)}
          onSubmit={handleSubmit}
          initialData={modal.mode === 'edit' ? modal.vehicle : null}
          allVehicles={ownerVehicles}
          uniquenessVehicles={vehicles}
          users={users}
          ownerId={ownerId}
          onOwnerChange={setOwnerId}
          companyCountry={ownerCountry}
        />
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden w-80">
            <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-700 text-white"><h3 className="font-semibold">{t('delete')}</h3></div>
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4">Delete this vehicle? This cannot be undone.</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50">{t('cancel') || 'Cancel'}</button>
                <button onClick={() => handleDelete(deleteId)} className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700">{t('delete')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
